<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-12">
    <h3 class="cf-title cf-title--3 cf-text-align-left">
      {{ titleContent }}
    </h3>

    <template v-if="isAsapPickupTimeOnly">
      <div
        v-if="isDelivery"
        class="cf-card cf-flex cf-flex--row cf-flex--align--center cf-flex--gap-16"
      >
        <span class="cf-icon far fa-clock" />

        <p class="cf-subtitle cf-subtitle--2">
          Your estimated delivery time is:

          <transition
            name="fade"
            mode="out-in"
          >
            <span :key="orderDetails.deliveryEstimate.deliveryTime">
              <b class="cf-no-wrap">
                {{ deliveryEstimateTimeContent }}
              </b>

              <b class="cf-no-wrap">
                ({{ orderDetails.deliveryEstimate.etaDisplay }})
              </b>
            </span>
          </transition>
        </p>
      </div>

      <template v-else>
        <div class="cf-card cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
          <template v-if="selectedTimestamp">
            <span class="cf-icon far fa-clock" />

            <p class="cf-subtitle cf-subtitle--2">
              Your order will be ready

              <b class="cf-no-wrap">
                {{ selectedTimestampContent }}
              </b>
            </p>
          </template>

          <template v-else>
            <div class="cf-loader cf-loader--small" />

            <p class="cf-subtitle cf-subtitle--2">
              Checking available pickup times...
            </p>
          </template>
        </div>
      </template>
    </template>

    <template v-else>
      <div class="cf-grid cf-grid--2-grid-column-equal cf-grid--gap-12">
        <cf-select-input
          label="Date"
          name="date"
          :searchable="false"
          :values="pickupDays"
          :model-value="day"
          :loading="isLoading"
          :validations="{
            required: true
          }"
          @update:model-value="dayInputSelectHandler"
        />

        <cf-select-input
          label="Time"
          name="time"
          :searchable="false"
          :model-value="selectedTimestamp"
          :values="timesForDay"
          :loading="isLoading"
          :validations="{
            required: true
          }"
          @update:model-value="setTimestamp"
        />
      </div>
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import momentCalendar from '@/filters/moment-calendar';

  import errorHandlers from '@/services/error-handlers';

  import CfSelectInput from '@/view/components/shared/cf-select-input/index.vue';


  export default {
    name: 'CfFulfillmentTimeSelect',

    components: {
      CfSelectInput
    },

    inject: {
      $validator: {
        from: '$validator'
      }
    },

    data() {
      return {
        isLoading: false,
        pickupDays: [],
        day: null,
        timesForDay: [],
        selectedTimestamp: null
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('menu', ['menu']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['orderDetails']),

      isAsapPickupTimeOnly() {
        return this.merchant.features['asap-pickup-only'];
      },

      isManualPickUpTimeSelectionRequired() {
        return this.selectedLocation.features.RequirePickUpTimeSelection;
      },

      isDelivery() {
        return this.orderDetails.fulfillmentType === 'delivery';
      },

      isRoomService() {
        return this.orderDetails.fulfillmentType === 'room-service';
      },

      titleContent() {
        return this.isDelivery || this.isRoomService
          ? 'Delivery Time'
          : 'Pickup Time';
      },

      selectedLocationTimezone() {
        return this.selectedLocation.timeZone;
      },

      deliveryEstimateTimeContent() {
        const { deliveryTime } = this.orderDetails.deliveryEstimate;

        return momentCalendar(deliveryTime, null, this.selectedLocationTimezone);
      },

      selectedTimestampContent() {
        return momentCalendar(this.selectedTimestamp, null, this.selectedLocationTimezone);
      }
    },

    watch: {
      'order.orderItemTotal': 'orderItemTotalWatcher'
    },

    mounted() {
      this.getPickupDays();
    },

    methods: {
      /* Important!!!
       * Be mindful of modifiying this method as the parent component is referecing it via: this.$refs.timeSelect.getPickupDays
       */
      async getPickupDays() {
        try {
          this.isLoading = true;

          const pickupDaysResult = await this.$store.dispatch('locations/getPickupDaysforSelectedLocation');

          if (pickupDaysResult.length) {
            await this.setPickupTimes(pickupDaysResult);
          }
        }
        catch (error) {
          errorHandlers.silent(error);
        }
        finally {
          this.isLoading = false;
        }
      },

      async setPickupTimes(pickupDays) {
        const firstAvailableDay = pickupDays[0];

        const asapTimestamp = firstAvailableDay.times[0].value;

        this.pickupDays = pickupDays;

        if (this.isAsapPickupTimeOnly) {
          this.setDayAndTimesForDay(firstAvailableDay);

          this.setTimestamp(asapTimestamp);
        }
        else if (this.isManualPickUpTimeSelectionRequired) {
          this.setDayAndTimesForDay(firstAvailableDay);

          this.setTimestamp(null);
        }
        else {
          const userSessionTimestamp = this.orderDetails.fulfillmentTime;

          const matchedSessionTimestamp = this.parsePickupDaysForTimestamp(pickupDays, userSessionTimestamp);

          const day = matchedSessionTimestamp.day ?? firstAvailableDay;

          const selectedTimestamp = matchedSessionTimestamp.timestamp
            ? matchedSessionTimestamp.timestamp
            : asapTimestamp;

          this.setDayAndTimesForDay(day);

          this.setTimestamp(selectedTimestamp);
        }
      },

      setDayAndTimesForDay(day) {
        this.day = day.value;

        this.timesForDay = day.times;
      },

      parsePickupDaysForTimestamp(pickupDays, sessionTimestamp) {
        const parsedResult = {};

        const _parsePickupDays = (pickupDay) => {
          const matchedTimestamp = pickupDay.times.find(d => d.value === sessionTimestamp);

          if (matchedTimestamp) {
            Object.assign(parsedResult, {
              day: pickupDay,
              timestamp: matchedTimestamp.value
            });
          }
        };

        pickupDays.forEach(_parsePickupDays);

        return parsedResult;
      },

      async setTimestamp(timestamp) {
        this.selectedTimestamp = timestamp;

        await this.$store.dispatch('orderAhead/setOrderFulfillmentTime', timestamp);
      },

      async orderItemTotalWatcher() {
        if (this.isDelivery) {
          const sessionDeliveryEstimateTimestamp = this.orderDetails.deliveryEstimate.deliveryTime;

          await this.$store.dispatch('orderAhead/setOrderFulfillmentTime', sessionDeliveryEstimateTimestamp);
        }
      },

      dayInputSelectHandler(day) {
        const matchedDay = this.pickupDays.find(d => d.value === day);

        const firstAvailableTimestamp = matchedDay.times[0].value;

        const timestamp = this.isManualPickUpTimeSelectionRequired
          ? null
          : firstAvailableTimestamp;

        this.setDayAndTimesForDay(matchedDay);

        this.setTimestamp(timestamp);
      }
    }
  };
</script>
